import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, setAuthToken, getAuthToken, removeAuthToken } from '../../../api/client';

/**
 * User type from API
 */
export interface User {
  id: string;
  email: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  profileImage?: string | null;
  emailVerified: boolean;
  isAdmin?: boolean;
  activeTimelineId?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Google credential response
 */
interface GoogleCredentialResponse {
  credential: string;
}

/**
 * Apple auth response
 */
interface AppleAuthResponse {
  authorization: {
    id_token: string;
  };
  user?: {
    name?: {
      firstName?: string;
      lastName?: string;
    };
    email?: string;
  };
}

/**
 * Signup result
 */
interface SignupResult {
  needsVerification: boolean;
  email: string;
}

/**
 * User export data
 */
export interface UserExportData {
  user: User;
  timelines: unknown[];
  settings: unknown;
}

/**
 * Timeline with subscriber info for deletion preview
 */
export interface TimelineWithSubscribers {
  id: string;
  name: string;
  subscriberCount: number;
}

/**
 * Deletion preview
 */
export interface DeletionPreview {
  ownedTimelines: number;
  subscribedTimelines: number;
  settings: boolean;
  canTransferTimelines: boolean;
  timelinesWithSubscribers: TimelineWithSubscribers[];
}

/**
 * Auth context value
 */
export interface AuthContextValue {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  pendingVerificationEmail: string | null;
  signup: (email: string, password: string) => Promise<SignupResult>;
  login: (email: string, password: string) => Promise<User>;
  loginWithGoogle: (credentialResponse: GoogleCredentialResponse) => Promise<User>;
  loginWithApple: (response: AppleAuthResponse) => Promise<User>;
  logout: () => Promise<void>;
  verifyEmail: (token: string) => Promise<User>;
  resendVerification: (email?: string) => Promise<boolean>;
  requestMagicLink: (email: string) => Promise<boolean>;
  verifyMagicLink: (token: string) => Promise<User>;
  clearPendingVerification: () => void;
  updateProfile: (displayName: string | null, firstName: string | null, lastName: string | null) => Promise<User>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  exportUserData: () => Promise<UserExportData>;
  getDeletionPreview: () => Promise<DeletionPreview>;
  deleteAccount: (confirmation: string, password: string | null, transferTimelines: boolean) => Promise<boolean>;
}

/**
 * API error with code
 */
interface ApiError extends Error {
  code?: string;
  email?: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getAuthToken();
      if (token) {
        try {
          // Verify token is still valid by fetching user info
          const response = await api.get<{ user: User }>('/auth/me');
          setCurrentUser(response.user);
        } catch (err) {
          // Token is invalid, remove it
          removeAuthToken();
          setCurrentUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Sign up with email and password
  // Returns { needsVerification: true, email } instead of logging in
  const signup = async (email: string, password: string): Promise<SignupResult> => {
    try {
      setError(null);
      const response = await api.post<{ email: string }>('/auth/register', { email, password });
      // New registration flow - no token returned, must verify email first
      setPendingVerificationEmail(response.email);
      return { needsVerification: true, email: response.email };
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  // Sign in with email and password
  const login = async (email: string, password: string): Promise<User> => {
    try {
      setError(null);
      const response = await api.post<{ token: string; user: User }>('/auth/login', { email, password });
      setAuthToken(response.token);
      setCurrentUser(response.user);
      return response.user;
    } catch (err) {
      const error = err as ApiError;
      // Check for email not verified error
      if (error.code === 'EMAIL_NOT_VERIFIED' && error.email) {
        setPendingVerificationEmail(error.email);
      }
      setError(error.message);
      throw err;
    }
  };

  // Sign in with Google
  const loginWithGoogle = async (credentialResponse: GoogleCredentialResponse): Promise<User> => {
    try {
      setError(null);
      const response = await api.post<{ token: string; user: User }>('/auth/google', {
        idToken: credentialResponse.credential
      });
      setAuthToken(response.token);
      setCurrentUser(response.user);
      return response.user;
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  // Sign in with Apple
  const loginWithApple = async (response: AppleAuthResponse): Promise<User> => {
    try {
      setError(null);
      const apiResponse = await api.post<{ token: string; user: User }>('/auth/apple', {
        idToken: response.authorization.id_token,
        user: response.user // Apple only provides this on first sign-in
      });
      setAuthToken(apiResponse.token);
      setCurrentUser(apiResponse.user);
      return apiResponse.user;
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  // Verify email with token from email link
  const verifyEmail = async (token: string): Promise<User> => {
    try {
      setError(null);
      const response = await api.post<{ token: string; user: User }>('/auth/verify-email', { token });
      setAuthToken(response.token);
      setCurrentUser(response.user);
      setPendingVerificationEmail(null);
      return response.user;
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  // Resend verification email
  const resendVerification = async (email?: string): Promise<boolean> => {
    try {
      setError(null);
      await api.post('/auth/resend-verification', { email: email || pendingVerificationEmail });
      return true;
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  // Request magic link for passwordless login
  const requestMagicLink = async (email: string): Promise<boolean> => {
    try {
      setError(null);
      await api.post('/auth/magic-link', { email });
      setPendingVerificationEmail(email);
      return true;
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  // Verify magic link token and login
  const verifyMagicLink = async (token: string): Promise<User> => {
    try {
      setError(null);
      const response = await api.post<{ token: string; user: User }>('/auth/verify-magic-link', { token });
      setAuthToken(response.token);
      setCurrentUser(response.user);
      setPendingVerificationEmail(null);
      return response.user;
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  // Clear pending verification email
  const clearPendingVerification = (): void => {
    setPendingVerificationEmail(null);
  };

  // Update user profile
  const updateProfile = async (displayName: string | null, firstName: string | null, lastName: string | null): Promise<User> => {
    try {
      setError(null);
      const response = await api.put<{ user: User }>(`/users/${currentUser!.id}/profile`, {
        displayName,
        firstName,
        lastName,
      });
      setCurrentUser(response.user);
      return response.user;
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  // Change password
  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      setError(null);
      await api.put(`/users/${currentUser!.id}/password`, {
        currentPassword,
        newPassword,
      });
      return true;
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  // Export user data
  const exportUserData = async (): Promise<UserExportData> => {
    try {
      setError(null);
      const data = await api.get<UserExportData>(`/users/${currentUser!.id}/export`);
      return data;
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  // Get deletion preview
  const getDeletionPreview = async (): Promise<DeletionPreview> => {
    try {
      setError(null);
      const preview = await api.get<DeletionPreview>(`/users/${currentUser!.id}/deletion-preview`);
      return preview;
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  // Delete account
  const deleteAccount = async (confirmation: string, password: string | null, transferTimelines: boolean): Promise<boolean> => {
    try {
      setError(null);
      await api.delete(`/users/${currentUser!.id}`, {
        confirmation,
        password: password || null,
        transferTimelines,
      });
      // Clear auth state
      removeAuthToken();
      setCurrentUser(null);
      return true;
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  // Sign out
  const logout = async (): Promise<void> => {
    try {
      setError(null);
      removeAuthToken();
      setCurrentUser(null);
      setPendingVerificationEmail(null);
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw err;
    }
  };

  const value: AuthContextValue = {
    currentUser,
    loading,
    error,
    pendingVerificationEmail,
    signup,
    login,
    loginWithGoogle,
    loginWithApple,
    logout,
    verifyEmail,
    resendVerification,
    requestMagicLink,
    verifyMagicLink,
    clearPendingVerification,
    updateProfile,
    changePassword,
    exportUserData,
    getDeletionPreview,
    deleteAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
